/**
 * Shared API client utilities for plugins
 *
 * Provides consistent authentication header management across all plugins
 */
/**
 * Storage keys for multi-account authentication
 * These keys match the accountManager in web-app
 */
const ACCOUNTS_KEY = 'accounts';
const ACTIVE_ACCOUNT_KEY = 'activeAccountEmail';
let redirecting = false; // guard against repeated/parallel redirects
/**
 * Clear the active multi-account session and redirect to /login.
 *
 * Mirrors the web-app shell's accountManager.clearAll() + axios 401 interceptor,
 * but lives in plugin-sdk so plugins can react to an expired/invalid token
 * without importing the web-app shell. Call this from a plugin's fetch wrapper
 * when a request returns HTTP 401.
 */
export const handleUnauthorized = () => {
    if (redirecting)
        return; // in-flight guard (parallel 401s -> one navigation)
    if (typeof window === 'undefined')
        return;
    if (window.location.pathname === '/login')
        return; // loop guard
    redirecting = true;
    try {
        localStorage.removeItem(ACCOUNTS_KEY);
        localStorage.removeItem(ACTIVE_ACCOUNT_KEY);
    }
    catch {
        /* ignore storage errors */
    }
    window.location.href = '/login';
};
/**
 * Get active account session from localStorage
 */
const getActiveSession = () => {
    const activeEmail = localStorage.getItem(ACTIVE_ACCOUNT_KEY);
    if (!activeEmail)
        return null;
    const accountsStr = localStorage.getItem(ACCOUNTS_KEY);
    if (!accountsStr)
        return null;
    try {
        const accounts = JSON.parse(accountsStr);
        return accounts[activeEmail] || null;
    }
    catch {
        return null;
    }
};
/**
 * Get authentication headers from localStorage
 *
 * Retrieves JWT Bearer token and company ID from multi-account storage
 * Used by plugins to authenticate with backend services
 *
 * @returns Headers object with Authorization and X-Company-ID if available
 *
 * @example
 * ```typescript
 * const response = await fetch('/api/endpoint', {
 *   headers: {
 *     ...getAuthHeaders(),
 *     'Content-Type': 'application/json',
 *   }
 * });
 * ```
 */
export const getAuthHeaders = () => {
    const headers = {};
    const session = getActiveSession();
    if (!session)
        return headers;
    if (session.accessToken) {
        headers['Authorization'] = `Bearer ${session.accessToken}`;
    }
    if (session.activeCompanyId) {
        headers['X-Company-ID'] = session.activeCompanyId;
    }
    return headers;
};
/**
 * Get standard API headers including auth and content-type
 *
 * @returns Headers object with Authorization, X-Company-ID, and Content-Type
 *
 * @example
 * ```typescript
 * const response = await fetch('/api/endpoint', {
 *   method: 'POST',
 *   headers: getApiHeaders(),
 *   body: JSON.stringify(data)
 * });
 * ```
 */
export const getApiHeaders = () => {
    return {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
    };
};
/**
 * Check if user is authenticated
 *
 * @returns true if access token exists in active session
 */
export const isAuthenticated = () => {
    const session = getActiveSession();
    return !!session?.accessToken;
};
/**
 * Get current company ID
 *
 * @returns Company ID string or null if not available
 */
export const getCompanyId = () => {
    const session = getActiveSession();
    return session?.activeCompanyId || null;
};
/**
 * Get current access token
 *
 * @returns Access token string or null if not available
 */
export const getAccessToken = () => {
    const session = getActiveSession();
    return session?.accessToken || null;
};
/**
 * Get the active session's effective permissions (as resolved for the active company).
 *
 * @returns Array of permission codes (empty if no active session)
 */
export const getPermissions = () => {
    const session = getActiveSession();
    return session?.user?.permissions ?? [];
};
/**
 * Whether a set of held permissions satisfies a required permission code,
 * mirroring the backend PermissionResolver wildcard semantics:
 *  - `*` grants everything
 *  - exact match (e.g. `order:sales:update`)
 *  - prefix wildcards: `order:*` grants `order:sales:read`; `order:sales:*` grants `order:sales:read`
 *
 * Pure (no I/O) so reactive callers (e.g. a route guard reading permissions from
 * React state) can reuse the exact same semantics. This is the single source of
 * truth for permission matching on the frontend; the backend remains the authority.
 *
 * @param userPermissions - The permissions the user holds
 * @param requiredPermission - The permission code to check for
 */
export const matchesPermission = (userPermissions, requiredPermission) => {
    if (userPermissions.includes('*'))
        return true;
    if (userPermissions.includes(requiredPermission))
        return true;
    const parts = requiredPermission.split(':');
    // Start one below full length: `order:sales:read:*` could never grant `order:sales:read`.
    for (let i = parts.length - 1; i > 0; i--) {
        if (userPermissions.includes(parts.slice(0, i).join(':') + ':*'))
            return true;
    }
    return false;
};
/**
 * Whether the active session holds a given permission (see {@link matchesPermission}).
 * Use to gate UI affordances (action buttons, sections, routes) — this only avoids
 * showing actions that would 403; the backend remains the authority.
 *
 * NOTE: this reads a localStorage snapshot at call time and is NOT reactive. It is
 * correct because account/company switches perform a full page reload (so every
 * component re-evaluates on mount). A caller that must react to a permission change
 * WITHOUT a reload should read `useAuth().user.permissions` and pass it to
 * {@link matchesPermission} instead (as RoutePermissionGuard does).
 */
export const hasPermission = (requiredPermission) => matchesPermission(getPermissions(), requiredPermission);
/**
 * Get current user from localStorage
 *
 * Retrieves the authenticated user object from the active session.
 * Used by plugins to access current user information without importing from main app.
 *
 * @returns User object or null if not authenticated
 *
 * @example
 * ```typescript
 * const user = getCurrentUser();
 * if (user) {
 *   console.log('Current user:', user.email);
 *   fetchUserSpecificData(user.id);
 * }
 * ```
 */
export const getCurrentUser = () => {
    const session = getActiveSession();
    return session?.user || null;
};
/**
 * Get current user ID
 *
 * Convenience method to get just the user ID without parsing full user object.
 *
 * @returns User ID string or null if not authenticated
 *
 * @example
 * ```typescript
 * const userId = getCurrentUserId();
 * if (userId) {
 *   fetchTasksByAssignee(userId);
 * }
 * ```
 */
export const getCurrentUserId = () => {
    const session = getActiveSession();
    return session?.user?.id || null;
};
